import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { API_URL } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger 
} from '../components/ui/sheet';
import { 
  Mail, 
  Zap, 
  Lock, 
  Clock, 
  MapPin, 
  Check, 
  X,
  Users
} from 'lucide-react';
import { useTranslation } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

const MatchInvitationsSheet = () => {
  const navigate = useNavigate();
  const { language } = useTranslation();
  const [open, setOpen] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);

  useEffect(() => {
    if (open) {
      loadInvitations();
    }
  }, [open]);

  // Load invitations when sheet opens (no polling)
  useEffect(() => {
    if (open) {
      loadInvitations();
    }
  }, [open]);

  const loadInvitations = async () => {
    try {
      const { data } = await api.get(`/invitations/match/received`, { withCredentials: true });
      setInvitations(data.invitations);
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  };

  const respondToInvitation = async (invitationId, accept) => {
    setRespondingTo(invitationId);
    try {
      const { data } = await api.post(
        `${API_URL}/invitations/match/${invitationId}/respond`,
        { accept },
        { withCredentials: true }
      );
      
      if (accept) {
        toast.success(language === 'fr' ? 'Vous avez rejoint le match !' : 'You joined the match!');
        if (data.matchId) {
          setOpen(false);
          navigate(`/matches/${data.matchId}`);
        }
      } else {
        toast.success(language === 'fr' ? 'Invitation refusée' : 'Invitation declined');
      }
      
      // Refresh invitations
      loadInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setRespondingTo(null);
    }
  };

  const getInvitationIcon = (type) => {
    switch (type) {
      case 'private':
        return <Lock size={16} className="text-[#9333EA]" />;
      case 'group_fcfs':
        return <Zap size={16} className="text-[#F59E0B]" />;
      default:
        return <Mail size={16} className="theme-accent" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingCount = invitations.length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="match-invitations-btn"
          className="relative theme-text hover:theme-accent"
        >
          <Mail size={24} />
          {pendingCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#9333EA] rounded-full flex items-center justify-center text-[10px] font-bold theme-text animate-pulse">
              {pendingCount > 9 ? '9+' : pendingCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      
      <SheetContent side="bottom" className="theme-bg border-t theme-border h-[80vh] rounded-t-3xl">
        <SheetHeader className="pb-4 border-b theme-border">
          <SheetTitle className="theme-text flex items-center gap-2">
            <Mail size={20} className="theme-accent" />
            {language === 'fr' ? 'Invitations aux matchs' : 'Match Invitations'}
            {pendingCount > 0 && (
              <span className="px-2 py-0.5 bg-[#9333EA] rounded-full text-xs">
                {pendingCount}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>
        
        <div className="mt-4 space-y-3 overflow-y-auto max-h-[calc(80vh-100px)]">
          {invitations.length === 0 ? (
            <div className="text-center py-12">
              <Mail size={48} className="mx-auto text-zinc-700 mb-4" />
              <p className="theme-text-secondary">
                {language === 'fr' ? 'Aucune invitation en attente' : 'No pending invitations'}
              </p>
            </div>
          ) : (
            invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="theme-bg-card border theme-border rounded-xl p-4 space-y-3"
              >
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {getInvitationIcon(invitation.invitationType)}
                    <span className="text-xs theme-text-secondary uppercase">
                      {invitation.invitationType === 'private' 
                        ? (language === 'fr' ? 'Match privé' : 'Private')
                        : invitation.invitationType === 'group_fcfs'
                        ? (language === 'fr' ? 'Premier arrivé' : 'First come')
                        : (language === 'fr' ? 'Invitation' : 'Invitation')
                      }
                    </span>
                  </div>
                  
                  {invitation.invitationType === 'group_fcfs' && invitation.spotsAvailable && (
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-[#F59E0B]/20 rounded-full">
                      <Users size={12} className="text-[#F59E0B]" />
                      <span className="text-xs font-bold text-[#F59E0B]">
                        {invitation.spotsAvailable} {language === 'fr' ? 'place(s)' : 'spot(s)'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Match info */}
                <div>
                  <h3 className="font-bold theme-text">{invitation.matchTitle || 'Match'}</h3>
                  <p className="text-sm theme-text-secondary">
                    {language === 'fr' ? 'De' : 'From'} {invitation.inviterName}
                    {invitation.groupName && (
                      <span className="text-[#F59E0B]"> • {invitation.groupName}</span>
                    )}
                  </p>
                </div>

                {/* Details */}
                <div className="flex flex-wrap gap-3 text-sm">
                  {invitation.matchDate && (
                    <div className="flex items-center gap-1 theme-text-secondary">
                      <Clock size={14} />
                      <span>{formatDate(invitation.matchDate)}</span>
                    </div>
                  )}
                  {invitation.matchLocation && (
                    <div className="flex items-center gap-1 theme-text-secondary">
                      <MapPin size={14} />
                      <span>{invitation.matchLocation}</span>
                    </div>
                  )}
                </div>

                {/* Message */}
                {invitation.message && (
                  <p className="text-sm text-zinc-300 italic">"{invitation.message}"</p>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => respondToInvitation(invitation.id, true)}
                    disabled={respondingTo === invitation.id}
                    className="flex-1 bg-[#00d4ff] text-black hover:bg-[#00b8d4]"
                    data-testid={`accept-invitation-${invitation.id}`}
                  >
                    <Check size={16} className="mr-1" />
                    {respondingTo === invitation.id 
                      ? '...' 
                      : (language === 'fr' ? 'Accepter' : 'Accept')
                    }
                  </Button>
                  <Button
                    onClick={() => respondToInvitation(invitation.id, false)}
                    disabled={respondingTo === invitation.id}
                    variant="outline"
                    className="flex-1 theme-border text-zinc-300 hover:theme-bg-card transition-colors duration-300"
                    data-testid={`decline-invitation-${invitation.id}`}
                  >
                    <X size={16} className="mr-1" />
                    {language === 'fr' ? 'Refuser' : 'Decline'}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MatchInvitationsSheet;
